<template>
  <section class="featured-properties-section py-16" v-if="featuredProperties.length > 0">
    <v-container>
      <!-- Section Header -->
      <div class="d-flex flex-column flex-md-row align-center justify-space-between mb-10">
        <div class="text-center text-md-left mb-4 mb-md-0">
          <v-chip
            color="primary"
            variant="flat"
            size="small"
            class="font-weight-bold mb-3 px-4"
          >
            OPORTUNIDADES DE INVERSIÓN
          </v-chip>
          <h2 class="section-title mb-2">
            Inmuebles <span class="text-primary">Destacados</span> en el norte de Cundinamarca
          </h2>
          <p class="section-subtitle text-grey-darken-1 mb-0">
            Propiedades exclusivas en venta y arriendo con estudio de títulos 100% verificado por León & Casallas.
          </p>
        </div>

        <v-btn
          color="primary"
          variant="outlined"
          size="large"
          class="font-weight-bold px-6"
          @click="$router.push('/properties')"
        >
          Ver Todos los Inmuebles
          <v-icon right class="ml-2">mdi-arrow-right</v-icon>
        </v-btn>
      </div>

      <!-- Properties Slider / Cards -->
      <div class="position-relative slider-container">
        <!-- Custom arrow navigation for desktop -->
        <div class="slider-nav d-none d-md-flex" v-if="featuredProperties.length > 3">
          <v-btn
            icon
            variant="flat"
            color="white"
            elevation="4"
            class="nav-btn prev-btn"
            @click="scrollSlider('left')"
          >
            <v-icon color="primary">mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn
            icon
            variant="flat"
            color="white"
            elevation="4"
            class="nav-btn next-btn"
            @click="scrollSlider('right')"
          >
            <v-icon color="primary">mdi-chevron-right</v-icon>
          </v-btn>
        </div>

        <!-- Scrollable cards track -->
        <div class="cards-track py-3 px-1" ref="track" :class="{ 'justify-md-center': featuredProperties.length < 4 }">
          <div
            v-for="property in featuredProperties"
            :key="property.id"
            class="card-item"
          >
            <div class="property-card d-flex flex-column h-100">
              <!-- Image Header -->
              <div class="card-image-wrapper position-relative bg-grey-darken-4 overflow-hidden">
                <v-img
                  :src="property.image"
                  height="220"
                  cover
                  class="position-absolute w-100 h-100 property-image"
                  style="filter: blur(10px) brightness(0.5); z-index: 0;"
                ></v-img>
                <v-img
                  :src="property.image"
                  :alt="property.title"
                  height="220"
                  class="property-image position-relative"
                  style="z-index: 1;"
                >
                  <template v-slot:placeholder>
                    <div class="d-flex align-center justify-center fill-height bg-transparent">
                      <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </div>
                  </template>
                </v-img>

                <!-- Badges overlay -->
                <div class="card-badges d-flex align-center justify-space-between w-100 pa-3">
                  <v-chip
                    :color="property.operation === 'Venta' ? 'primary' : 'grey-darken-4'"
                    size="small"
                    variant="flat"
                    class="font-weight-bold text-uppercase"
                  >
                    {{ property.operation }}
                  </v-chip>
                  <v-chip
                    color="white"
                    size="small"
                    variant="flat"
                    class="font-weight-bold text-grey-darken-3"
                  >
                    <v-icon left size="14" color="primary" class="mr-1">mdi-map-marker</v-icon>
                    {{ property.city }}
                  </v-chip>
                </div>

                <!-- Code tag -->
                <div class="code-tag text-caption font-weight-bold">
                  {{ property.code }}
                </div>
              </div>

              <!-- Content Body -->
              <div class="card-body pa-5 d-flex flex-column flex-grow-1">
                <!-- Price -->
                <div class="text-h6 font-weight-black text-primary mb-2">
                  {{ property.priceFormatted }}
                </div>

                <!-- Title -->
                <h3 class="property-title font-weight-bold text-grey-darken-4 mb-2">
                  {{ property.title }}
                </h3>

                <!-- Location -->
                <div class="d-flex align-center text-caption text-grey-darken-1 mb-4">
                  <v-icon size="16" color="grey-darken-1" class="mr-1">mdi-map-marker-outline</v-icon>
                  <span class="text-truncate">{{ property.location }}</span>
                </div>

                <!-- Specs row -->
                <div class="specs-grid d-flex align-center justify-space-between py-3 mb-5 border-top-bottom flex-wrap gap-2">
                  <div class="spec-item d-flex align-center" v-if="property.bedrooms > 0">
                    <v-icon size="18" color="primary" class="mr-1">mdi-bed-outline</v-icon>
                    <span class="text-caption font-weight-bold">{{ property.bedrooms }} Hab</span>
                  </div>
                  <div class="spec-item d-flex align-center" v-if="property.bathrooms > 0">
                    <v-icon size="18" color="primary" class="mr-1">mdi-shower</v-icon>
                    <span class="text-caption font-weight-bold">{{ property.bathrooms }} Baños</span>
                  </div>
                  <div class="spec-item d-flex align-center" v-if="property.area > 0">
                    <v-icon size="18" color="primary" class="mr-1">mdi-texture-box</v-icon>
                    <span class="text-caption font-weight-bold" title="Área">{{ property.area }} m²</span>
                  </div>
                  <div class="spec-item d-flex align-center" v-if="property.builtArea > 0">
                    <v-icon size="18" color="primary" class="mr-1">mdi-ruler-square</v-icon>
                    <span class="text-caption font-weight-bold" title="Área Construida">{{ property.builtArea }} m²</span>
                  </div>
                  <div class="spec-item d-flex align-center">
                    <v-icon size="18" color="primary" class="mr-1">mdi-home-outline</v-icon>
                    <span class="text-caption font-weight-bold">{{ property.type }}</span>
                  </div>
                </div>

                <!-- Button view details -->
                <v-btn
                  block
                  color="primary"
                  variant="flat"
                  class="mt-auto font-weight-bold"
                  @click="openDetail(property)"
                >
                  Ver Detalle
                  <v-icon right size="18" class="ml-2">mdi-open-in-new</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>
    </v-container>
  </section>
</template>

<script>
import { fetchAvailableProperties } from '@/services/propertiesService';

export default {
  name: 'FeaturedProperties',
  data() {
    return {
      properties: [],
      loading: false
    };
  },
  computed: {
    featuredProperties() {
      // Devolvemos 5 inmuebles destacados (featured === true)
      return this.properties.filter((p) => p.featured).slice(0, 5);
    },
  },
  async mounted() {
    this.loading = true;
    try {
      this.properties = await fetchAvailableProperties();
    } finally {
      this.loading = false;
    }
  },
  methods: {
    scrollSlider(direction) {
      const track = this.$refs.track;
      if (!track) return;
      const scrollAmount = 360;
      if (direction === 'left') {
        track.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      } else {
        track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }
    },
    openDetail(property) {
      // Navegamos al listado de propiedades pasando el id en el query string para abrir el detalle sin perder contexto
      this.$router.push({ name: 'properties', query: { id: property.id } });
    },
  },
};
</script>

<style scoped>
.featured-properties-section {
  background-color: #f7f9fc;
  position: relative;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.section-title {
  font-size: clamp(1.8rem, 3.5vw, 2.6rem);
  font-weight: 900;
  line-height: 1.2;
  color: #1a1a1a;
}

.section-subtitle {
  font-size: 1.05rem;
  line-height: 1.6;
}

/* Slider Track CSS */
.cards-track {
  display: flex;
  gap: 24px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
  padding-bottom: 8px;
}

.cards-track::-webkit-scrollbar {
  display: none; /* Chrome/Safari */
}

.card-item {
  flex: 0 0 350px;
  max-width: 350px;
  scroll-snap-align: start;
}

@media (max-width: 600px) {
  .card-item {
    flex: 0 0 310px;
    max-width: 310px;
  }
}

.property-card {
  background: #ffffff;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.07);
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.property-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.12);
  border-color: rgba(169, 7, 7, 0.25);
}

.card-image-wrapper {
  overflow: hidden;
  height: 220px;
}

.property-image {
  transition: transform 0.6s ease;
}

.property-card:hover .property-image {
  transform: scale(1.08);
}

.card-badges {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
}

.code-tag {
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.65);
  color: #ffffff;
  padding: 3px 10px;
  border-radius: 6px;
  backdrop-filter: blur(4px);
  z-index: 2;
}

.property-title {
  font-size: 1.1rem;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  height: 48px;
}

.border-top-bottom {
  border-top: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
}

/* Nav arrows */
.slider-nav .nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  background-color: white !important;
}

.prev-btn {
  left: -20px;
}

.next-btn {
  right: -20px;
}

.slider-nav .nav-btn:hover {
  background-color: #A90707 !important;
}

.slider-nav .nav-btn:hover .v-icon {
  color: #ffffff !important;
}
</style>
